import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { Plus } from "lucide-react";
import { PageLayout } from "@/components/templates/page-layout";
import { SearchBar } from "@/components/molecules/search-bar";
import { SiteCard } from "@/components/molecules/site-card";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/molecules/confirm-dialog";
import { useSites } from "@/core/hooks/useSites";
import { offlineDb } from "@/core/offline/db";

export default function SitesPage() {
  const navigate = useNavigate();
  const { sites, deleteSite } = useSites();
  const [search, setSearch] = useState("");
  const [siteToDelete, setSiteToDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Compteurs de zones par site
  const zoneCounts =
    useLiveQuery(async () => {
      const counts: Record<string, number> = {};
      for (const site of await offlineDb.sites.toArray()) {
        counts[site.id] = await offlineDb.zones
          .where("siteId")
          .equals(site.id)
          .count();
      }
      return counts;
    }) ?? {};

  // Sites clôturés (pour les filtrer de la liste : ils vont dans /campagnes)
  const closedSiteIds = useLiveQuery(async () => {
    const allCampagnes = await offlineDb.campagnes.toArray();
    const bySiteId = new Map<string, { hasActive: boolean; count: number }>();
    for (const c of allCampagnes) {
      const prev = bySiteId.get(c.siteId) ?? { hasActive: false, count: 0 };
      prev.count++;
      if (c.status === "active") prev.hasActive = true;
      bySiteId.set(c.siteId, prev);
    }
    const ids = new Set<string>();
    for (const [siteId, info] of bySiteId) {
      if (!info.hasActive && info.count > 0) ids.add(siteId);
    }
    return ids;
  }) ?? new Set<string>();

  const filteredSites = useMemo(() => {
    // Exclure les sites clôturés (ils sont dans /campagnes)
    const activeSites = sites.filter((s) => !closedSiteIds.has(s.id));
    if (!search.trim()) return activeSites;
    const q = search.toLowerCase();
    return activeSites.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.client?.toLowerCase().includes(q) ||
        s.address?.toLowerCase().includes(q)
    );
  }, [sites, search, closedSiteIds]);

  // Scroll lock + Escape quand le modal est ouvert
  useEffect(() => {
    if (!siteToDelete) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setSiteToDelete(null); setDeleteError(""); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [siteToDelete]);

  const handleConfirmDelete = useCallback(async () => {
    if (!siteToDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteSite(siteToDelete);
      setSiteToDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  }, [siteToDelete, deleteSite]);

  return (
    <PageLayout title="Sites">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex-1">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Rechercher un site..."
          />
        </div>
        <Button onClick={() => navigate("/sites/nouveau")}>
          <Plus className="size-4" />
          Ajouter
        </Button>
      </div>

      {filteredSites.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          {sites.length === 0
            ? "Aucun site pour l'instant. Créez votre premier site !"
            : "Aucun résultat pour cette recherche."}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredSites.map((site) => (
            <SiteCard
              key={site.id}
              id={site.id}
              name={site.name}
              client={site.client ?? undefined}
              address={site.address ?? undefined}
              zoneCount={zoneCounts[site.id] ?? 0}
              createdAt={site.createdAt}
              onEdit={() => navigate(`/sites/${site.id}/modifier`)}
              onDelete={() => setSiteToDelete(site.id)}
            />
          ))}
        </div>
      )}
      {/* Modale de confirmation de suppression */}
      <ConfirmDialog
        open={!!siteToDelete}
        title="Supprimer ce site ?"
        description="Le site et toutes ses zones, campagnes, points et mesures seront définitivement supprimés. Cette action est irréversible."
        error={deleteError}
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setSiteToDelete(null); setDeleteError(""); }}
      />
    </PageLayout>
  );
}
